import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import DashboardLayout from "./components/DashboardLayout";
import DashboardHome from "./components/DashboardHome";
import AnimalDetailPage from "./components/AnimalDetailPage";
import DraftAnimalsPage from "./components/DraftAnimalsPage";
import ActiveAnimalsPage from "./components/ActiveAnimalsPage";
import InactiveAnimalsPage from "./components/InactiveAnimalsPage";
import MilkProductionPage from "./components/MilkingSessionsPage";
import Users from "./components/Users";
import EmployeesPage from "./components/Employee";
import AddRole from "./components/Role";
import Profile from "./components/Profile";
import Settings from "./components/Settings";
import SalesManagement from "./components/SalesManagement";
import CustomerForm from "./components/CustomerForm";
import ProductForm from "./components/ProductForm";
import InvoiceForm from "./components/InvoiceForm";
import Receipt from "./components/Receipt";



import InvoiceList from "./components/InvoiceList";
import InvoiceView from "./components/InvoiceView";
import ReceiptList from "./components/ReceiptList";
import ReceiptForm from "./components/ReceiptForm";
import ReceiptView from "./components/ReceiptView";

import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/Register";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PayrollPage from "./components/Setups/PayrollPage";
import PayrollProcessingPage from "./components/Payroll/PayrollProcessingPage";
import ProductionBatchesPage from "./components/Production/ProductionBatchesPage";
import CalvingEventsPage from "./components/Production/CalvingEventsPage";
import ReproductionEventsPage from "./components/Production/ReproductionEventsPage";
import HealthRecordsPage from "./components/Health/HealthRecordsPage";
import MedicationInventoryPage from "./components/Health/MedicationInventoryPage";
import MedicationUsagePage from "./components/Health/MedicationUsagePage";
import ProductionBatchDetailPage from "./components/Production/ProductionBatchDetailPage";
import ProductionReportPage from "./components/Production/ProductionReportPage";


// 🚀 Optional: Import only if these components exist
// import ProductionBatchesPage from "./components/ProductionBatchesPage";
// import CalvingEventsPage from "./components/CalvingEventsPage";
// import ReproductionEventsPage from "./components/ReproductionEventsPage";
// import HealthRecordsPage from "./components/HealthRecordsPage";
// import MedicationsPage from "./components/MedicationsPage";
// import MedicationUsagePage from "./components/MedicationUsagePage";
// import SalesPage from "./components/SalesPage";
// import SaleItemsPage from "./components/SaleItemsPage";
// import DisposalsPage from "./components/DisposalsPage";
// import LocationsPage from "./components/LocationsPage";
// import AuditLogsPage from "./components/AuditLogsPage";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  // 🔐 Check authentication on load
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      setIsAuthenticated(true);
      // You could load user info from localStorage or an API here
      setCurrentUser({ username: "Demo User", role: "Admin" });
    }
    setIsLoading(false);
  }, []);

  const handleLogin = (userData) => {
    localStorage.setItem("authToken", "dummy-token");
    setIsAuthenticated(true);
    setCurrentUser(userData || { username: "Demo User", role: "Admin" });
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    setIsAuthenticated(false);
    setCurrentUser(null);
  };

  const handleUpdateUser = (updatedUserData) => {
    setCurrentUser((prev) => ({ ...prev, ...updatedUserData }));
  };

  // 🧩 Protected Route
  const ProtectedRoute = ({ children }) => {
    if (isLoading) {
      return (
        <div style={loadingStyle}>
          Loading...
        </div>
      );
    }
    return isAuthenticated ? children : <Navigate to="/login" replace />;
  };

  // 🌐 Public Route
  const PublicRoute = ({ children }) => {
    if (isLoading) {
      return (
        <div style={loadingStyle}>
          Loading...
        </div>
      );
    }
    return !isAuthenticated ? children : <Navigate to="/dashboard" replace />;
  };

  const loadingStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    fontSize: "1.2rem",
  };

  // 🧭 Dashboard Wrapper
  const DashboardWrapper = ({ children }) => (
    <DashboardLayout onLogout={handleLogout} user={currentUser}>
      {children}
    </DashboardLayout>
  );

  return (
    <Router>
      <Routes>
        {/* ---------- PUBLIC ROUTES ---------- */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage onLogin={handleLogin} />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage onRegister={handleLogin} />
            </PublicRoute>
          }
        />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* ---------- PROTECTED ROUTES ---------- */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <DashboardHome />
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />

        {/* Profile and Settings */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <Profile user={currentUser} onUpdateUser={handleUpdateUser} />
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <Settings user={currentUser} />
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />

        {/* Animal Management */}
        <Route
          path="/animals/draft"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <DraftAnimalsPage />
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/animals/active"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <ActiveAnimalsPage />
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/animals/inactive"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <InactiveAnimalsPage />
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/animals/:id"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <AnimalDetailPage />
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />

        {/*  Production */}
        <Route
          path="/milking-sessions"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <MilkProductionPage />
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/production-batches"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <ProductionBatchesPage/>
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/calving-events"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <CalvingEventsPage/>
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />
         <Route
          path="/reproduction-events"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <ReproductionEventsPage/>
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/production_report"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <ProductionReportPage/>
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />


{/* HEALTH */}
          <Route
          path="/health-records"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <HealthRecordsPage/>
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/medications"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <MedicationInventoryPage/>
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/medication-usage"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <MedicationUsagePage/>
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />
        {/* Employee, Roles, Users */}
        <Route
          path="/employees"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <EmployeesPage />
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <Users />
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/roles"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <AddRole />
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />

        {/* Sales */}
        <Route
          path="/sales"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <SalesManagement />
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />
        {/* PayrollPage */}
          <Route
          path="/payroll"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <PayrollProcessingPage/>
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />

          <Route
          path="/payroll-setup"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <PayrollPage/>
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />

        {/* Sales forms */}
        <Route path="/customer-form" element={<CustomerForm />} />
        <Route path="/customer-form/:id" element={<CustomerForm />} />
        <Route path="/product-form" element={<ProductForm />} />
        <Route path="/product-form/:id" element={<ProductForm />} />
        <Route path="/invoice-form" element={<InvoiceList />} />
        <Route path="/invoice-form/:id" element={<InvoiceForm />} />
        <Route path="/receipt" element={<Receipt />} />
        <Route path="/production-batches/:id" element={<ProductionBatchDetailPage/>} />


         
        <Route path="/receipts" element={<ReceiptList />} />
        <Route path="/receipts/new" element={<ReceiptForm />} />
        <Route path="/receipts/:id" element={<ReceiptForm />} />
        <Route path="/receipt-view/:id" element={<ReceiptView />} />
        <Route path="/receipt-form/:id" element={<ReceiptForm />} />
        {/* <Route path="/invoice-form/new" element={<InvoiceForm />} />
        <Route path="/invoice-form/:id" element={<InvoiceForm />} />
        <Route path="/invoice-form/view/:id" element={<InvoiceView />} /> */}
        <Route path="/invoice-form/new" element={<InvoiceForm />} />
        <Route path="/invoice-form/view/:id" element={<InvoiceView />} />
        {/* <Route path="/invoice-form/view/:id" element={<InvoiceForm />} /> */}
        <Route path="/invoice-form/edit/:id" element={<InvoiceForm />} />

          {/* Setups */}
          <Route
          path="/payroll-setup"
          element={
            <ProtectedRoute>
              <DashboardWrapper>
                <PayrollPage/>
              </DashboardWrapper>
            </ProtectedRoute>
          }
        />

        {/* ---------- DEFAULT ROUTES ---------- */}
        <Route
          path="/"
          element={
            <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;

